// btw-front/src/pages/PasswordReset.jsx
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal, message } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { requestPasswordReset, confirmPasswordReset, lookupPasswordReset } from "../api/auth";

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-md">
            {token ? <ConfirmForm token={token} /> : <RequestForm />}
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

function RequestForm() {
  const { t } = useTranslation();
  const [loginId, setLoginId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (submitting) return;
    const trimmed = loginId.trim().toLowerCase();
    if (!trimmed) {
      message.warning(t("partners.passwordReset.errors.loginIdRequired"));
      return;
    }
    setSubmitting(true);
    try {
      // 1) 등록 이메일(마스킹) 조회 → 2) 확인 다이얼로그 → 3) 확인 시 발송
      const { email } = await lookupPasswordReset(trimmed);
      Modal.confirm({
        title: t("partners.passwordReset.confirmSendTitle"),
        content: (
          <div>
            <p>
              {t("partners.passwordReset.registeredEmailLabel")}: <strong>{email}</strong>
            </p>
            <p className="mt-2">{t("partners.passwordReset.confirmSendDesc")}</p>
          </div>
        ),
        okText: t("partners.passwordReset.confirmSendOk"),
        cancelText: t("partners.passwordReset.confirmSendCancel"),
        onOk: () =>
          requestPasswordReset(trimmed)
            .then(() => setSent(true))
            .catch((err) => {
              const msg =
                (err && err.payload && err.payload.error && err.payload.error.message) ||
                t("partners.passwordReset.errors.requestFailed");
              message.error(msg);
              throw err; // 발송 실패 시 다이얼로그 유지
            }),
      });
    } catch (err) {
      const code = err && err.payload && err.payload.error && err.payload.error.code;
      const msg =
        code === "USER_NOT_FOUND"
          ? t("partners.passwordReset.errors.userNotFound")
          : (err && err.payload && err.payload.error && err.payload.error.message) ||
            t("partners.passwordReset.errors.requestFailed");
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <h1 className="text-xl font-bold text-slate-900">{t("partners.passwordReset.title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {t("partners.passwordReset.requestSent")}
        </p>
        <div className="mt-6 flex justify-end">
          <Link to="/partners" className="btw-btn-ghost">
            ← {t("partners.passwordReset.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm"
    >
      <h1 className="text-xl font-bold text-slate-900">{t("partners.passwordReset.title")}</h1>
      <p className="mt-2 text-sm text-slate-600">{t("partners.passwordReset.requestDesc")}</p>
      <div className="mt-6">
        <Input
          label={t("partners.passwordReset.loginId")}
          required
          autoComplete="username"
          value={loginId}
          onChange={(e) => setLoginId(e.target.value.toLowerCase())}
          maxLength={40}
        />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        <Link to="/partners" className="btw-btn-ghost">
          ← {t("partners.passwordReset.backToLogin")}
        </Link>
        <Button type="submit" variant="accent" disabled={submitting}>
          {submitting
            ? t("partners.passwordReset.submitting")
            : t("partners.passwordReset.requestSubmit")}
        </Button>
      </div>
    </form>
  );
}

function ConfirmForm({ token }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (submitting) return;
    if (newPassword.length < 8) {
      message.warning(t("partners.passwordReset.errors.passwordTooShort"));
      return;
    }
    if (newPassword !== confirm) {
      message.warning(t("partners.passwordReset.errors.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, newPassword);
      message.success(t("partners.passwordReset.confirmSuccess"));
      navigate("/partners", { replace: true });
    } catch (err) {
      const code = err && err.payload && err.payload.error && err.payload.error.code;
      let msg;
      if (code === "INVALID_TOKEN" || code === "TOKEN_EXPIRED" || code === "TOKEN_USED") {
        msg = t("partners.passwordReset.errors.tokenInvalid");
      } else {
        msg =
          (err && err.payload && err.payload.error && err.payload.error.message) ||
          t("partners.passwordReset.errors.confirmFailed");
      }
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm"
    >
      <h1 className="text-xl font-bold text-slate-900">{t("partners.passwordReset.title")}</h1>
      <p className="mt-2 text-sm text-slate-600">{t("partners.passwordReset.confirmDesc")}</p>
      <div className="mt-6 grid gap-4">
        <Input
          label={t("partners.passwordReset.newPassword")}
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          maxLength={128}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          hint={t("partners.passwordReset.passwordHint")}
        />
        <Input
          label={t("partners.passwordReset.confirmPassword")}
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          maxLength={128}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        <Link to="/partners" className="btw-btn-ghost">
          ← {t("partners.passwordReset.backToLogin")}
        </Link>
        <Button type="submit" variant="accent" disabled={submitting}>
          {submitting
            ? t("partners.passwordReset.submitting")
            : t("partners.passwordReset.confirmSubmit")}
        </Button>
      </div>
    </form>
  );
}
